<template>
  <div class="w-700 q-mx-auto">
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      :class="{ 'q-px-none q-pb-none': dropdownListView }"
      data-test-id="site-location-lookup"
    >
      <template
        v-if="googleMapsEnabled && locationZones.length"
      >
        <q-input
          ref="addressInput"
          class="autocomplete-wrapper autocomplete-idle col "
          :class="{ 'q-mx-sm': !dropdownListView }"
          color="responsive"
          :outlined="fieldType !== TEXT_FIELD_TYPES.UNDERLINE"
          :rounded="fieldType === TEXT_FIELD_TYPES.ROUNDED"
          :square="fieldType === TEXT_FIELD_TYPES.SQUARE"
          :bg-color="fieldType == TEXT_FIELD_TYPES.UNDERLINE || fieldOutlined ? undefined : 'responsive' "
          clearable
          autogrow
          dense
          :autofocus="dropdownListView"
        >
          <template #prepend>
            <q-icon name="fas fa-magnifying-glass" />
          </template>

          <vue-google-autocomplete
            id="map"
            ref="addressAutocomplete"
            classname="address-autocomplete form-control text-responsive"
            style="background: transparent;"
            :country="countries"
            :fields="['address_components','geometry', 'formatted_address']"
            types=""
            :placeholder="$t('form.enterAddressForAutocompletePrompt')"
            @placechanged="placeSelect"
          />
          <template
            v-if="compactView"
            #append
          >
            <use-current-location-button
              class="self-center"
              padding="sm"
              :outline="buttonType === BUTTON_TYPES.OUTLINE"
              :class="getClasses(buttonType, buttonColor, buttonColorHex)"
              :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
              :push="buttonType === BUTTON_TYPES.STANDARD"
              icon-only
              @current-location-request="handleCurrentLocationRequest"
              @current-location-response="handleCurrentLocationResult"
            />
          </template>
        </q-input>
      </template>
      <template
        v-else
      >
        <q-input
          v-model="locationSearch"
          class="col q-mx-sm q-mb-sm"
          color="responsive"
          :outlined="fieldType !== TEXT_FIELD_TYPES.UNDERLINE"
          :rounded="fieldType === TEXT_FIELD_TYPES.ROUNDED"
          :square="fieldType === TEXT_FIELD_TYPES.SQUARE"
          :bg-color="fieldType == TEXT_FIELD_TYPES.UNDERLINE || fieldOutlined ? undefined : 'responsive' "
          clearable
          dense
          :autofocus="dropdownListView"
          :label="$t('search.for.location')"
        >
          <template #prepend>
            <q-icon name="fas fa-magnifying-glass" />
          </template>
        </q-input>
      </template>
    </component>
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      v-if="!compactView && googleMapsEnabled && locationZones.length"
      class="row"
      :class="{
        'q-px-none q-pt-none': dropdownListView,
        'q-mb-md': !dropdownListView
      }"
    >
      <use-current-location-button
        class="self-start q-ma-sm"
        padding="xs sm"
        :size="dropdownListView ? 'sm' : 'md'"
        :outline="buttonType === BUTTON_TYPES.OUTLINE"
        :class="{
          [getClasses(buttonType, buttonColor, buttonColorHex)]: true,
          'full-width': dropdownListView
        }"
        :style="getStyles(buttonType, buttonColor, buttonColorHex, buttonBorderRadius)"
        :push="buttonType === BUTTON_TYPES.STANDARD"
        @current-location-request="handleCurrentLocationRequest"
        @current-location-response="handleCurrentLocationResult"
      />
    </component>
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      v-if="!!closestLocations && closestLocations.length > 0"
      :class="{
        'q-mx-sm': isWidget,
        'no-padding full-width': dropdownListView
      }"
    >
      <site-selector-locations-list
        :class="{ 'no-padding full-width no-border': dropdownListView }"
        :dense="dropdownListView"
        :sites-without-location-zone-id="currentClosestLocations"
        :location-zones="currentClosestLocations"
        :site-locations="currentClosestLocations"
        :all-sites-have-images="allSitesHaveImages"
        :show-site-image="showSiteImage"
        :show-site-description="showSiteDescription"
        :card-color="cardColor"
        :card-color-hex="cardColorHex"
        :card-border-radius="cardBorderRadius"
        :redirect-home-page="redirectHomePage"
        :is-widget="isWidget"
        :dropdown-list-view="dropdownListView"
        :hide-address="hideAddress"
        show-distance
        hide-zone-header
      >
        <template #title>
          <span
            :class="dropdownListView ? 'row text-body1 text-responsive-7 text-center q-px-md q-pt-sm' : 'text-h5'"
          >
            {{ $tc('siteLocation.closestLocation', 2) }}
          </span>
        </template>
        <template #action>
          <div :class="{ 'row justify-between': dropdownListView }">
            <q-btn
              class="col q-ma-sm"
              color="primary"
              :label="$t('actions.loadMore')"
              @click="showMore"
            />
            <q-btn
              class="col q-ma-sm"
              color="secondary"
              :label="$t('nav.actions.showAll')"
              @click="clearPlaceSelect"
            />
          </div>
        </template>
      </site-selector-locations-list>
    </component>
    <component
      :is="dropdownListView ? 'q-item' : 'div'"
      v-if="hasLocationSearchFilter"
      :class="isWidget ? 'q-mx-sm' : undefined"
    >
      <div
        v-if="filterSiteLocations.length < 1"
        class="text-subtitle1 text-left q-my-sm q-mx-md"
      >
        {{ $t('search.noResults') }}
      </div>
      <site-selector-locations-list
        :sites-without-location-zone-id="filterSiteLocations"
        :site-locations="filterSiteLocations"
        :all-sites-have-images="allSitesHaveImages"
        :show-site-image="showSiteImage"
        :show-site-description="showSiteDescription"
        :card-color="cardColor"
        :card-color-hex="cardColorHex"
        :card-border-radius="cardBorderRadius"
        :redirect-home-page="redirectHomePage"
        :is-widget="isWidget"
        :dropdown-list-view="dropdownListView"
        :hide-address="hideAddress"
        hide-zone-header
      />
    </component>
  </div>
</template>

<script>
import { googleMapsEnabled, region } from 'src/config'
import { BUTTON_TYPES, SELECTOR_DISPLAY_TYPES, TEXT_FIELD_TYPES } from 'src/models/page/Section'
import SiteLocationsListMixin from 'src/modules/consumer/components/mixins/SiteLocationsListMixin'
import SiteSelectorLocationsList from 'src/modules/consumer/components/site-selector/SiteSelectorLocationsList'
import UseCurrentLocationButton from 'src/modules/consumer/components/site-selector/UseCurrentLocationButton'
import VueGoogleAutocomplete from 'vue-google-autocomplete'
export default {
  name: 'SiteLocationLookUp',
  components: {
    VueGoogleAutocomplete,
    UseCurrentLocationButton,
    SiteSelectorLocationsList
  },
  mixins: [SiteLocationsListMixin],
  props: {
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    cardColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    cardBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    cardColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    compactView: {
      type: Boolean,
      required: false,
      default: false
    },
    fieldType: {
      type: String,
      required: false,
      default: 'filled'
    },
    fieldOutlined: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showSiteDescription: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultExpanded: {
      type: Boolean,
      required: false,
      default: false
    },
    redirectHomePage: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonType: {
      type: String,
      required: false,
      default: BUTTON_TYPES.OUTLINE
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    buttonBorderRadius: {
      type: Number,
      required: false,
      default: 4
    },
    isWidget: {
      type: Boolean,
      required: false,
      default: false
    },
    dropdownListView: {
      type: Boolean,
      required: false,
      default: false
    },
    hideAddress: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['all-locations-shown', 'loading'],
  data () {
    return {
      mapsService: null,
      closestLocations: null,
      closestLocationBatchSize: 3,
      closestLocationQuantity: 0,
      searchboxFocus: false,
      locationSearch: ''
    }
  },
  computed: {
    hasLocationSearchFilter () {
      return !!this.locationSearch
    },
    filterSiteLocations () {
      if (!this.hasLocationSearchFilter) return this.siteLocations
      return _.filter(this.siteLocations, site => _.includes(_.toLower(site.name), _.toLower(this.locationSearch)))
    },
    currentClosestLocations () {
      return this.closestLocations?.slice(0, this.closestLocationQuantity) || []
    },
    countries () {
      // some countries have multiple iso31661 codes separated by commas
      const countries = _(this.sites.data)
        .map(site => _.map(site.iso31661.split(','), countryCode => countryCode.toLowerCase().trim()))
        .flatten()
        .uniq()
        .value()
      // google maps autocomplete only supports up to 5 countries
      return countries.length > 5 ? [] : countries
    }

  },
  watch: {
    hasLocationSearchFilter (value) {
      if (value === true) {
        this.$emit('all-locations-shown', false)
      } else {
        this.$emit('all-locations-shown', true)
      }
    }
  },
  created () {
    _.extend(this, {
      region,
      googleMapsEnabled,
      TEXT_FIELD_TYPES,
      SELECTOR_DISPLAY_TYPES,
      BUTTON_TYPES
    })
  },
  mounted () {
    if (!this.mapsService) {
      this.mapsService = new window.google.maps.DistanceMatrixService()
    }
  },
  unmounted () {
    this.mapsService = null
  },

  methods: {
    clearPlaceSelect () {
      this.closestLocationQuantity = 0
      this.$refs.addressAutocomplete.clear()
      this.closestLocations = null
      this.$emit('all-locations-shown', true)
    },
    showMore () {
      this.closestLocationQuantity += this.closestLocationBatchSize
    },
    async placeSelect (__, placeData) {
      this.$emit('loading', true)
      this.closestLocations = null
      this.closestLocationQuantity = this.closestLocationBatchSize
      try {
        this.closestLocations = await this.$getClosestLocations(placeData.formatted_address, this.siteLocations)
        this.$emit('all-locations-shown', false)
      } catch (error) {
        this.$mh.showErrors(error)
      }
      this.$emit('loading', false)
    },
    handleCurrentLocationRequest () {
      this.$emit('loading', true)
      this.clearPlaceSelect()
      this.closestLocations = null
      this.closestLocationQuantity = this.closestLocationBatchSize
    },
    async handleCurrentLocationResult (origin) {
      if (origin) {
        this.closestLocations = await this.$getClosestLocations(origin, this.siteLocations)
      }
      this.$emit('loading', false)
    }
  }
}
</script>
<style lang="scss" scoped>
.w-700 {
  width: 100%;
  max-width: 700px;
}

:deep(.address-display) {
  padding-top: 24px;
  padding-bottom: 8px;
}

:deep(.address-autocomplete) {
  border: none;
  width: 100%
}

:deep(.address-autocomplete:focus) {
  outline: none;
}

:deep(.autocomplete-wrapper) {
  .q-field__native {
    display: none;
  }
}

:deep(.pac-container, .pac-item) {
    /* GOOGLE MAPS AUTOCOMPLETE DROPDOWN IN ADMIN DOESN'T DISPLAY CORRECTLY WITHOUT THIS */
    z-index: 10000 !important;
 }

</style>
